import { DirectRtcClient } from './direct-rtc';
import { VideoSequenceGate } from './video-sequence';
import { decodeBinaryFrame, encodeInputEvent, encodeKeyframeRequest, BinaryFrame, FrameType, TouchInputPayload } from './protocol';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
export type TransportMode = 'local' | 'webrtc' | 'relay';

export interface TransportCallbacks {
  onServerStateChange: (state: ConnectionState) => void;
  onPhoneStateChange: (connected: boolean) => void;
  onStreamStateChange: (active: boolean) => void;
  onSessionStateChange: (paired: boolean) => void;
  onRttUpdate: (rttMs: number) => void;
  onFrame: (frame: BinaryFrame) => void;
  onError: (error: string) => void;
  onTransportModeChange?: (mode: TransportMode) => void;
  onLocalRedirectAvailable?: (url: string) => void;
}

export class DriveDeckTransportClient {
  private ws: WebSocket | null = null;
  private localWs: WebSocket | null = null;
  private serverUrl: string;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private lastPingSent = 0;
  private inputSequence = 0;
  private isDestroyed = false;
  private callbacks: TransportCallbacks;

  private readonly rtc: DirectRtcClient;
  private rtcSignaling = false;

  private transportMode: TransportMode = 'relay';
  private localEndpoint = '';
  private isProbingLocal = false;
  private streamActive = false;
  private readonly videoSequence = new VideoSequenceGate(() => this.requestKeyframe());
  private localProbeRetryTimer: number | null = null;

  constructor(serverUrl: string, callbacks: TransportCallbacks) {
    this.serverUrl = serverUrl;
    this.callbacks = callbacks;
    this.rtc = new DirectRtcClient({
      sendSignal: message => this.sendControl(message),
      onOpen: () => {
        const oldSocket = this.localWs;
        this.localWs = null;
        this.isProbingLocal = false;
        this.transportMode = 'webrtc';
        oldSocket?.close();
        this.videoSequence.reset();
        this.stopLocalProbeRetries();
        this.callbacks.onTransportModeChange?.('webrtc');
        this.requestKeyframe();
      },
      onPacket: packet => {
        if (this.transportMode !== 'webrtc' || !this.streamActive) return;
        const frame = decodeBinaryFrame(packet);
        if (frame && this.videoSequence.accept(frame)) this.callbacks.onFrame(frame);
      },
      onClose: () => {
        if (this.transportMode !== 'webrtc') return;
        this.transportMode = 'relay';
        this.videoSequence.reset();
        this.callbacks.onTransportModeChange?.('relay');
        if (this.streamActive && !this.isDestroyed) {
          this.requestKeyframe();
          this.startLocalProbeRetries();
        }
      },
      onRtt: rtt => { if (this.transportMode === 'webrtc') this.callbacks.onRttUpdate(rtt); },
    });
    this.token = localStorage.getItem('drivedeck_session_token');

    // If viewer is loaded directly from a local IP/port (e.g. port 8080 or RFC1918 private IP)
    const isLocalHost = window.location.port === '8080' ||
      window.location.hostname === '192.168.43.1' ||
      window.location.hostname.startsWith('10.') ||
      window.location.hostname.startsWith('192.168.') ||
      window.location.hostname.endsWith('.local');

    if (isLocalHost) {
      const port = window.location.port || '8080';
      this.localEndpoint = `ws://${window.location.hostname}:${port}/ws/stream`;
      if (window.location.port === '8080') {
        this.transportMode = 'local';
      }
    }
  }

  public setToken(token: string) {
    this.token = token;
    localStorage.setItem('drivedeck_session_token', token);
    this.callbacks.onSessionStateChange(true);
  }

  public getToken(): string | null {
    return this.token;
  }

  public clearToken() {
    this.token = null;
    localStorage.removeItem('drivedeck_session_token');
    this.callbacks.onSessionStateChange(false);
  }

  public getTransportMode(): TransportMode {
    return this.transportMode;
  }

  public connect() {
    if (this.isDestroyed || (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING))) {
      return;
    }

    // In direct local mode (e.g. loaded directly from phone HTTP on port 8080), bypass VPS and connect to local WebSocket
    if (this.transportMode === 'local' && this.localEndpoint && window.location.port === '8080') {
      this.callbacks.onServerStateChange('connecting');
      this.probeLocalTransport();
      return;
    }

    this.callbacks.onServerStateChange(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');

    try {
      const url = new URL(this.serverUrl, window.location.href);
      url.protocol = url.protocol.replace('http', 'ws');
      if (this.token) {
        url.searchParams.set('token', this.token);
      }

      this.ws = new WebSocket(url.toString());
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        this.videoSequence.reset();
        this.reconnectAttempts = 0;
        this.callbacks.onServerStateChange('connected');
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          const frame = decodeBinaryFrame(event.data);
          if (frame) {
            if (frame.type === FrameType.HEARTBEAT_PONG) {
              const rtt = Date.now() - this.lastPingSent;
              this.callbacks.onRttUpdate(rtt);
            } else if (this.transportMode === 'relay') {
              // Only consume VPS video frames when in relay mode
              if (this.videoSequence.accept(frame)) this.callbacks.onFrame(frame);
            }
          }
        } else if (typeof event.data === 'string') {
          // JSON control packet
          try {
            const msg = JSON.parse(event.data);
            this.handleControlMessage(msg);
          } catch (e) {
            console.warn('[DriveDeck] Failed to parse control message:', e);
          }
        }
      };

      this.ws.onclose = () => {
        this.stopHeartbeat();
        this.streamActive = false;
        this.rtc.stop();
        this.callbacks.onServerStateChange('disconnected');
        this.callbacks.onPhoneStateChange(false);
        this.callbacks.onStreamStateChange(false);
        this.scheduleReconnect();
      };

      this.ws.onerror = (err) => {
        console.warn('[DriveDeck] WebSocket error:', err);
        this.callbacks.onError('WebSocket connection error');
      };
    } catch (e) {
      console.error('[DriveDeck] Connect error:', e);
      this.scheduleReconnect();
    }
  }

  private handleControlMessage(msg: Record<string, unknown>) {
    if (typeof msg.type === 'string' && msg.type.startsWith('rtc_')) {
      void this.rtc.handleSignal(msg);
    } else if (msg.type === 'pong' && typeof msg.timestamp === 'number') {
      // JSON pong echoed from server — measure one-way RTT from the original ping timestamp
      const rtt = Date.now() - msg.timestamp;
      if (this.transportMode === 'relay') this.callbacks.onRttUpdate(rtt);
    } else if (msg.type === 'session_status') {
      const paired = !!msg.paired;
      if (!paired && this.token) {
        // Phone re-registered or session revoked — drop stale token so pairing UI returns.
        this.clearToken();
        this.ws?.close();
        return;
      }
      this.callbacks.onSessionStateChange(paired);
      this.callbacks.onPhoneStateChange(!!msg.phoneConnected);
      if (!msg.streamActive) this.videoSequence.reset();
      this.streamActive = !!msg.streamActive;
      this.rtcSignaling = msg.rtcSignaling === true;
      if (!this.streamActive || !msg.phoneConnected) this.rtc.stop();
      this.callbacks.onStreamStateChange(this.streamActive);

      const advertised = typeof msg.localEndpoint === 'string' ? msg.localEndpoint.trim() : '';
      if (advertised.startsWith('ws://') || advertised.startsWith('wss://')) {
        if (advertised !== this.localEndpoint) {
          console.log('[DriveDeck] Phone advertised local endpoint:', advertised);
          const previousEndpoint = this.localEndpoint;
          this.localEndpoint = advertised;
          // Drop any stale local socket bound to the previous IP/url.
          if (this.localWs) {
            this.localWs.close();
            this.localWs = null;
          }
          if (this.transportMode === 'local') {
            this.videoSequence.reset();
            this.transportMode = 'relay';
            this.callbacks.onTransportModeChange?.('relay');
          }
          // If WebRTC is currently negotiating with an old or empty STUN URL, restart with the fresh endpoint
          if (this.streamActive && this.transportMode === 'relay' && previousEndpoint !== '') {
            this.rtc.stop();
          }
        }
      }

      // If loaded from public HTTPS and phone advertises a local hotspot IP on port 8080:
      // Auto-redirect the browser directly to local HTTP viewer for zero-latency, zero-cellular-data streaming.
      if (window.location.protocol === 'https:' && advertised.startsWith('ws://')) {
        try {
          const parsed = new URL(advertised);
          const host = parsed.hostname;
          const port = parsed.port || '8080';
          const isPrivateIp = host.startsWith('10.') || host.startsWith('192.168.') || host.startsWith('172.') || host.endsWith('.local');
          if (isPrivateIp && host !== 'car.drivedeck.tommasoberti.com') {
            const localHttpUrl = `http://${host}:${port}/`;
            this.callbacks.onLocalRedirectAvailable?.(localHttpUrl);

            // Auto-redirect to local Wi-Fi viewer unless user explicitly chose to stay on relay
            if (sessionStorage.getItem('drivedeck_stay_on_relay') !== 'true') {
              console.log('[DriveDeck] Local Wi-Fi endpoint detected, auto-redirecting to direct local viewer:', localHttpUrl);
              window.location.replace(localHttpUrl);
              return;
            }
          }
        } catch (e) {
          console.warn('[DriveDeck] Failed to parse advertised local endpoint for redirect:', e);
        }
      }

      // Probe direct local transport when streaming is active
      if (this.streamActive && this.transportMode === 'relay') {
        if (this.rtcSignaling) void this.rtc.start(this.resolveStunUrl());
        this.probeLocalTransport();
        this.startLocalProbeRetries();
      } else {
        this.stopLocalProbeRetries();
      }
    } else if (msg.type === 'auth_error') {
      this.clearToken();
      this.ws?.close();
      this.callbacks.onError(typeof msg.message === 'string' ? msg.message : 'Authentication required');
    }
  }

  /**
   * Attempt to establish direct local transport with the Android phone hotspot.
   * If successful, shifts media plane to direct local Wi-Fi with sub-15ms latency.
   */
  public probeLocalTransport() {
    if (this.isProbingLocal || this.localWs || this.isDestroyed || this.transportMode === 'webrtc') return;
    this.isProbingLocal = true;
    console.log('[DriveDeck] Probing local transport at', this.localEndpoint);

    try {
      const socket = new WebSocket(this.localEndpoint);
      this.localWs = socket;
      socket.binaryType = 'arraybuffer';

      const probeTimeout = window.setTimeout(() => {
        if (socket.readyState !== WebSocket.OPEN) {
          socket.close();
          this.isProbingLocal = false;
        }
      }, 2000);

      socket.onopen = () => {
        clearTimeout(probeTimeout);
        if (this.localWs !== socket || this.isDestroyed || this.transportMode === 'webrtc') { socket.close(); return; }
        this.isProbingLocal = false;
        this.localWs = socket;
        this.streamActive = true;
        this.videoSequence.reset();
        this.transportMode = 'local';
        this.stopLocalProbeRetries();
        console.log('[DriveDeck] Direct local hotspot transport connected!', this.localEndpoint);
        this.callbacks.onServerStateChange('connected');
        this.callbacks.onPhoneStateChange(true);
        this.callbacks.onSessionStateChange(true);
        this.callbacks.onStreamStateChange(true);
        this.callbacks.onTransportModeChange?.('local');
        this.requestKeyframe();
      };

      socket.onmessage = (event) => {
        if (this.localWs === socket && this.transportMode === 'local' && event.data instanceof ArrayBuffer) {
          const frame = decodeBinaryFrame(event.data);
          if (frame) {
            if (this.videoSequence.accept(frame)) this.callbacks.onFrame(frame);
          }
        }
      };

      socket.onclose = () => {
        clearTimeout(probeTimeout);
        this.isProbingLocal = false;
        if (this.localWs !== socket) return;
        this.localWs = null;
        if (this.transportMode === 'local') {
          if (window.location.protocol === 'http:' || window.location.port === '8080') {
            this.callbacks.onServerStateChange('disconnected');
            this.callbacks.onStreamStateChange(false);
            this.startLocalProbeRetries();
          } else {
            console.warn('[DriveDeck] Local transport lost, falling back seamlessly to VPS relay');
            this.videoSequence.reset();
            this.transportMode = 'relay';
            this.callbacks.onTransportModeChange?.('relay');
            this.requestKeyframe();
            if (this.streamActive) {
              this.startLocalProbeRetries();
            }
          }
        }
      };

      socket.onerror = () => {
        clearTimeout(probeTimeout);
        this.isProbingLocal = false;
        if (this.localWs === socket) socket.close();
      };
    } catch (e) {
      this.isProbingLocal = false;
      console.warn('[DriveDeck] Failed to initiate local transport probe:', e);
    }
  }

  private resolveStunUrl(): string | null {
    if (!this.localEndpoint) return null;
    try {
      const url = new URL(this.localEndpoint);
      const host = url.hostname;
      if (host && host !== 'car.drivedeck.tommasoberti.com') {
        return `stun:${host}:3478`;
      }
    } catch {}
    return null;
  }

  private startLocalProbeRetries() {
    if (this.localProbeRetryTimer !== null || this.isDestroyed) return;
    this.localProbeRetryTimer = window.setInterval(() => {
      if (!this.streamActive || this.transportMode === 'webrtc' || this.isDestroyed) {
        this.stopLocalProbeRetries();
        return;
      }
      if (this.rtcSignaling) void this.rtc.start(this.resolveStunUrl());
      if (this.transportMode === 'relay') this.probeLocalTransport();
    }, 5000);
  }

  private stopLocalProbeRetries() {
    if (this.localProbeRetryTimer !== null) {
      clearInterval(this.localProbeRetryTimer);
      this.localProbeRetryTimer = null;
    }
  }

  public sendInput(input: TouchInputPayload) {
    if (!this.streamActive) return;
    const packet = encodeInputEvent(input, this.inputSequence++);
    if (this.transportMode === 'webrtc' && this.rtc.send(packet)) return;
    if (this.transportMode === 'local' && this.localWs && this.localWs.readyState === WebSocket.OPEN) {
      this.localWs.send(packet);
    } else if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(packet);
    }
  }

  public requestKeyframe() {
    const packet = encodeKeyframeRequest(this.inputSequence++);
    if (this.transportMode === 'webrtc' && this.rtc.send(packet)) return;
    if (this.localWs && this.localWs.readyState === WebSocket.OPEN) {
      this.localWs.send(packet);
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(packet);
    }
  }

  public sendControl(msg: Record<string, unknown>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.pingTimer = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.lastPingSent = Date.now();
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: this.lastPingSent }));
      }
    }, 1500);
  }

  private stopHeartbeat() {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /** Explicit user/phone-driven unpair — clears token and stops reconnect. */
  public unpair() {
    this.clearToken();
    this.reconnectAttempts = 0;
    this.streamActive = false;
    this.rtc.stop();
    this.stopLocalProbeRetries();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.localWs) {
      this.localWs.close();
      this.localWs = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.transportMode = 'relay';
    this.callbacks.onTransportModeChange?.('relay');
  }

  private scheduleReconnect() {
    if (this.isDestroyed || this.reconnectTimer !== null) return;
    if (!this.token) return;
    this.reconnectAttempts++;
    if (this.reconnectAttempts > 2) {
      this.clearToken();
      return;
    }
    const delay = Math.min(200 * Math.pow(2, this.reconnectAttempts - 1), 2000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  public destroy() {
    this.isDestroyed = true;
    this.streamActive = false;
    this.rtc.stop();
    this.stopHeartbeat();
    this.stopLocalProbeRetries();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.localWs) {
      this.localWs.close();
      this.localWs = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}
